// Shared SEO schema helpers — single source of truth for sitewide org details.
export const SITE_URL = process.env.REACT_APP_SITE_URL || "https://optimumhospitalitysolutions.com";

export const COMPANY = {
    legalName: "Optimum Hospitality Solutions Ltd",
    name: "Optimum Hospitality Solutions",
    registrationNumber: "17286143",
    email: "info@optimumhospitalitysolutions.com",
    telephone: "+44 20 3996 2562",
    // Registered office — Suite RA01, 195–197 Wood Street, London E17 3NU.
    // Coordinates are deliberately on-published-address (NAP-consistent across
    // website, GBP, Bing Places, Apple Business Connect and Companies House).
    geo: { latitude: 51.5859, longitude: -0.019 },
    hasMap: "https://maps.google.com/?q=Suite+RA01,+195-197+Wood+Street,+London+E17+3NU",
    address: {
        streetAddress: "Suite RA01, 195–197 Wood Street",
        addressLocality: "London",
        postalCode: "E17 3NU",
        addressCountry: "GB",
        addressRegion: "Greater London",
    },
    // Operating hours published to LocalBusiness schema + GBP.
    // Mon–Fri 08:00–19:00, Saturday by appointment, closed Sunday.
    openingHours: [
        { days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "08:00", closes: "19:00" },
        { days: ["Saturday"], opens: "09:00", closes: "17:00", note: "by appointment" },
    ],
    sameAs: ["https://www.linkedin.com/in/sidney-a-a7ab35b7"],
};

const registrationIdentifier = {
    "@type": "PropertyValue",
    propertyID: "GB-COH",
    name: "Companies House Registration Number",
    value: COMPANY.registrationNumber,
};

const openingHoursSpecification = COMPANY.openingHours.map((slot) => ({
    "@type": "OpeningHoursSpecification",
    dayOfWeek: slot.days,
    opens: slot.opens,
    closes: slot.closes,
    ...(slot.note ? { description: slot.note } : {}),
}));

// UK service area as an array of administrativeArea entries — gives Google a
// richer signal than a single "United Kingdom" string for nationwide operators.
const areaServedUK = [
    { "@type": "Country", name: "United Kingdom" },
    { "@type": "AdministrativeArea", name: "England" },
    { "@type": "AdministrativeArea", name: "Scotland" },
    { "@type": "AdministrativeArea", name: "Wales" },
    { "@type": "AdministrativeArea", name: "Northern Ireland" },
    { "@type": "City", name: "London" },
];

export const organisationSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": `${SITE_URL}/#organization`,
    name: COMPANY.name,
    legalName: COMPANY.legalName,
    url: SITE_URL,
    logo: `${SITE_URL}/logo.png`,
    image: `${SITE_URL}/og-image.png`,
    description:
        "Operational consultancy and performance improvement support for hotels, hospitality businesses and multi-site operators throughout the UK.",
    address: { "@type": "PostalAddress", ...COMPANY.address },
    telephone: COMPANY.telephone,
    email: COMPANY.email,
    areaServed: areaServedUK,
    identifier: registrationIdentifier,
    sameAs: COMPANY.sameAs,
    knowsAbout: [
        "Hotel operations consultancy",
        "Hospitality performance improvement",
        "Labour productivity optimisation",
        "Guest experience strategy",
        "Hospitality operational audits",
        "Hotel pre-opening setup",
        "Multi-site hospitality operations",
        "Interim hospitality management",
    ],
};

export const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${SITE_URL}/#localbusiness`,
    name: COMPANY.legalName,
    legalName: COMPANY.legalName,
    url: SITE_URL,
    logo: `${SITE_URL}/logo.png`,
    image: `${SITE_URL}/og-image.png`,
    telephone: COMPANY.telephone,
    email: COMPANY.email,
    priceRange: "££££",
    currenciesAccepted: "GBP",
    paymentAccepted: "Bank Transfer, Invoice",
    address: { "@type": "PostalAddress", ...COMPANY.address },
    geo: { "@type": "GeoCoordinates", latitude: COMPANY.geo.latitude, longitude: COMPANY.geo.longitude },
    hasMap: COMPANY.hasMap,
    openingHoursSpecification,
    areaServed: areaServedUK,
    identifier: registrationIdentifier,
    sameAs: COMPANY.sameAs,
    slogan: "Operator-led hospitality consultancy.",
};

